import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-white rounded-xl px-3 py-2 shadow-warm-md border border-surface-200 text-sm">
      <p className="font-semibold text-ink-800">{p.name}</p>
      <p className="text-ink-500">{p.value?.toLocaleString()} users</p>
    </div>
  );
};

const PlatformSplitCard = ({ todayStats }) => {
  const webOnly = todayStats?.webOnlyUsers || 0;
  const mobileOnly = todayStats?.mobileOnlyUsers || 0;
  const multi = todayStats?.multiPlatformUsers || 0;
  const pushOptIn = todayStats?.pushOptInRate || 0;

  const data = [
    { name: 'Web only', value: webOnly, color: '#1B6B4A' },
    { name: 'Mobile only', value: mobileOnly, color: '#F5C842' },
    { name: 'Both platforms', value: multi, color: '#3BA676' },
  ].filter((d) => d.value > 0);

  const total = webOnly + mobileOnly + multi;
  const multiPct = total > 0 ? (multi / total) * 100 : 0;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-ink-900 text-[0.9375rem]">Platform split</h3>
        <span className="text-xs text-ink-400">All-time users</span>
      </div>
      <p className="text-xs text-ink-400 mb-5">
        Web-only vs mobile-only vs multi-platform users; push-notification opt-in rate.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div className="relative">
          {data.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={65}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {data.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} stroke="white" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[160px] flex items-center justify-center text-xs text-ink-400">
              No data yet
            </div>
          )}
        </div>

        <div className="space-y-2 self-center">
          {data.map((d) => (
            <div key={d.name} className="flex items-center gap-2 text-sm">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
              <span className="text-ink-700 font-medium">{d.name}</span>
              <span className="ml-auto text-ink-500 tabular-nums font-semibold">{d.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 pt-5 border-t border-surface-100 grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Web → Mobile</p>
          <p className="font-display text-2xl font-semibold text-ink-900 mt-1">
            {multiPct.toFixed(0)}%
          </p>
          <p className="text-xs text-ink-400">Use both platforms</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Push opt-in</p>
          <p className="font-display text-2xl font-semibold text-ink-900 mt-1">
            {Math.round(pushOptIn * 100)}%
          </p>
          <p className="text-xs text-ink-400">Of mobile users</p>
        </div>
      </div>
    </div>
  );
};

export default PlatformSplitCard;
